import { useMemo } from 'react';
import { SonarQubeData } from './types';
import { useRepositoryOnboardingContext } from '../../context/RepositoryOnboardingContext';
import { hasService } from '../../hooks/useTdmsServicesData';

// Hook that uses shared data from RepositoryOnboarding context to avoid duplicate API calls
export const useSonarQubeData = (
  repositoryValue: string,
): { data: SonarQubeData | null; loading: boolean } => {
  const {
    integrationData: nebulaData,
    integrationLoading,
    tdmsServices,
    tdmsLoading,
  } = useRepositoryOnboardingContext();

  // Combine loading states - we need both nebula and TDMS data
  const loading = integrationLoading || tdmsLoading;

  const data = useMemo((): SonarQubeData | null => {
    if (!repositoryValue || !nebulaData?.SonarQube) {
      return null;
    }

    // Wait for TDMS data to load before deciding whether to show card
    if (tdmsLoading) {
      return null;
    }

    const hasCiCdService = hasService(tdmsServices, 'nebula-ci-cd');

    const provider = hasCiCdService ? 'nebula' : '<unknown-provider>';

    const sonarQubeIntegration = nebulaData.SonarQube;

    // Don't show the card if we don't have any information
    if (!sonarQubeIntegration.projectKey && !sonarQubeIntegration.source) {
      return null;
    }

    return {
      provider: provider, // Always nebula as requested
      projectKey: sonarQubeIntegration.projectKey || '',
      source: sonarQubeIntegration.source || '',
    };
  }, [repositoryValue, nebulaData, tdmsServices, tdmsLoading]);

  return { data, loading };
};
